// SandboxBanner.tsx — Phase 2.B
//
// Persistent banner shown across the app when the practice is in a pre-BAA
// status (sandbox/onboarding/pending_baa). Reminds the user that real PHI
// cannot be entered until they activate, and gives them a one-click CTA.
//
// Mounted globally in AppShell. Reads practice.status from useAuthStore.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../store';
import type { PracticeStatus } from '../../types';

const PRE_BAA_STATUSES: PracticeStatus[] = ['onboarding', 'sandbox', 'pending_baa'];

const STATUS_COPY: Record<string, { label: string; cta: string; tone: 'warning' | 'info' }> = {
  onboarding: {
    label: 'Your onboarding wizard is incomplete. Real patient data is blocked until you finish setup and activate.',
    cta: 'Resume onboarding',
    tone: 'warning',
  },
  sandbox: {
    label: 'You\'re in Sandbox mode. Explore RCMTask with demo patients — real patient data is blocked until you activate ($250).',
    cta: 'Activate to use real patients',
    tone: 'info',
  },
  pending_baa: {
    label: 'Your activation request is in progress. Once your BAA is signed, you\'ll be able to enter real patient data.',
    cta: 'View activation status',
    tone: 'info',
  },
};

export default function SandboxBanner() {
  const navigate = useNavigate();
  const { currentPractice } = useAuthStore();
  const status = currentPractice?.status as PracticeStatus | undefined;

  if (!status || !PRE_BAA_STATUSES.includes(status)) {
    return null;
  }

  const copy = STATUS_COPY[status];
  if (!copy) return null;

  const baseClass =
    copy.tone === 'warning'
      ? 'bg-amber-50 border-b border-amber-200 text-amber-900'
      : 'bg-[#F4F1EE] border-b border-[#C94B1F]/20 text-[#C94B1F]';

  const handleCta = () => {
    if (status === 'onboarding') navigate('/onboarding');
    else navigate('/activate'); // Phase 2.E will own this route
  };

  return (
    <div className={`${baseClass} px-4 py-2 flex items-center gap-3 text-sm`}>
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 leading-snug">
        <strong className="font-semibold uppercase tracking-wide text-xs mr-2">
          {status === 'onboarding' ? 'Setup incomplete' : status === 'sandbox' ? 'Sandbox mode' : 'Pending BAA'}
        </strong>
        {copy.label}
      </span>
      <button
        onClick={handleCta}
        className="flex items-center gap-1 text-xs font-semibold underline hover:no-underline whitespace-nowrap"
      >
        {copy.cta} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

/**
 * Helper: returns true if the current practice is in a state where real PHI
 * cannot be entered. UI components can use this to disable inputs / show
 * "DEMO ONLY" labels on PHI forms.
 */
export function useIsSandboxMode(): boolean {
  const { currentPractice } = useAuthStore();
  const status = currentPractice?.status as PracticeStatus | undefined;
  return !!status && PRE_BAA_STATUSES.includes(status);
}

/**
 * Inline label component shown on PHI forms when in sandbox mode. Renders
 * nothing in active/enterprise. Drop into any patient/appointment/task form.
 */
export function DemoOnlyLabel({ className = '' }: { className?: string }) {
  const isSandbox = useIsSandboxMode();
  if (!isSandbox) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded ${className}`}
    >
      Demo only
    </span>
  );
}
